<template>
  <div class="shipping-range-editor">
    <v-layout wrap>
      <v-flex xs12 class="d-flex align-center">
        <v-subheader>{{ $t('shippingDimensionsAndMaterials') }}</v-subheader>
        <v-btn 
          small 
          color="primary" 
          @click="addRange"
          v-if="!readonly"
        >
          {{ $t('add') }}
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout v-for="(range, index) in localRanges" :key="index" wrap class="shipping-range">
      <v-flex xs12>
        <v-card flat>
          <v-card-text class="pa-2">
            <v-layout wrap align-center pl-3 pr-4>
              <v-flex xs3 pr-2>
                <v-layout>
                  <v-flex xs6 pr-1>
                    <v-text-field
                      v-model.number="range.min"
                      :label="$t('minQuantity')"
                      dense
                      :disabled="readonly"
                      :rules="readonly ? [] : [
                        fieldIsRequired('minQuantity'),
                        fieldIsOverZero('minQuantity'),
                        fieldIsInteger('minQuantity'),
                        getScopeValidator('min', index)
                      ]"
                    />
                  </v-flex>
                  <v-flex xs6 pl-1>
                    <v-text-field
                      v-model.number="range.max"
                      :label="$t('maxQuantity')"
                      dense
                      :disabled="readonly"
                      :rules="readonly ? [] : [
                        fieldIsRequired('maxQuantity'),
                        fieldIsOverZero('maxQuantity'),
                        fieldIsInteger('maxQuantity'),
                        getScopeValidator('max', index)
                      ]"
                    />
                  </v-flex>
                  <v-divider vertical class="divider-height"></v-divider>
                </v-layout>
              </v-flex>

              <v-flex xs9>
                <v-layout wrap>
                  <v-flex xs3 pr-3>
                    <v-text-field
                      v-model.number="range.weight"
                      :label="$t('weight')"
                      :suffix="weightSuffix"
                      dense
                      :hint="$t('avgItemWeight')"
                      :disabled="readonly"
                      :rules="readonly ? [] : [fieldIsRequired('weight'), fieldIsOverZero('weight')]"
                    />
                  </v-flex>
                  <v-flex xs3 pr-3>
                    <v-text-field
                      v-model.number="range.length"
                      :label="$t('L')"
                      :suffix="sizeSuffix"
                      dense
                      :disabled="readonly"
                      :rules="readonly ? [] : [fieldIsRequired('length'), fieldIsOverZero('length')]"
                    />
                  </v-flex>
                  <v-flex xs3 pr-3>
                    <v-text-field
                      v-model.number="range.width"
                      :label="$t('W')"
                      :suffix="sizeSuffix"
                      dense
                      :disabled="readonly"
                      :rules="readonly ? [] : [fieldIsRequired('width'), fieldIsOverZero('width')]"
                    />
                  </v-flex>
                  <v-flex xs3 pr-3>
                    <v-text-field
                      v-model.number="range.height"
                      :label="$t('H')"
                      :suffix="sizeSuffix"
                      dense
                      :disabled="readonly"
                      :rules="readonly ? [] : [fieldIsRequired('height'), fieldIsOverZero('height')]"
                    />
                  </v-flex>
                </v-layout>
              </v-flex>

              <v-flex xs11 v-if="!readonly">
                <v-layout wrap>
                  <v-flex xs4>
                    <v-select
                      v-model="range.materials[0]"
                      :items="materialFees"
                      item-text="name"
                      item-value="name"
                      dense
                      :readonly="readonly"
                      clearable
                      return-object
                      :label="$t('materialFee')"
                      hide-details
                    />
                  </v-flex>
                  <v-flex xs3>
                    <v-text-field
                      v-if="!!range.materials && !!range.materials[0] && !!range.materials[0].name"
                      :value="range.materials[0].price"
                      :label="$t('price')"
                      readonly
                      dense
                      prefix="$"
                    />
                  </v-flex>
                  <v-flex xs2>
                    <v-text-field
                      v-if="!!range.materials && !!range.materials[0] && !!range.materials[0].name"
                      v-model.number="range.materials[0].quantity"
                      :label="$t('quantity')"
                      :readonly="readonly"
                      :rules="[fieldIsRequired('quantity'), fieldIsOverZero('quantity'), fieldIsInteger('quantity')]"
                      dense
                    />
                  </v-flex>
                </v-layout>
              </v-flex>

              <v-flex xs1 class="text-right">
                <v-btn 
                  icon 
                  small 
                  @click="removeRange(index)" 
                  v-if="localRanges.length > 1 && !readonly"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { checkRules } from '@/utils/tools'
import { measurementUnits, weightUnits } from '@/utils/const'

export default {
  name: 'ShippingRangeEditor',

  mixins: [checkRules],

  props: {
    value: {
      type: Array,
      default: () => []
    },
    materialFees: {
      type: Array,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    unit: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      localRanges: this.initRanges()
    }
  },

  computed: {
    sizeSuffix () {
      return measurementUnits[this.unit]
    },

    weightSuffix () {
      return weightUnits[this.unit]
    }
  },

  watch: {
    localRanges: {
      deep: true,
      handler (val) {
        this.$emit('input', val)
      }
    }
  },

  methods: {
    initRanges () {
      if (this.value?.length) {
        return this.value
      }

      return [this.initRange()]
    },
    initRange () {
      return {
        min: 1,
        max: null,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        materials: [{
          name: null,
          price: 0,
          quantity: 1
        }]
      }
    },
    addRange () {
      const lastRange = this.localRanges[this.localRanges.length - 1]
      const newRange = this.initRange()
      if (lastRange && lastRange.max) {
        newRange.min = lastRange.max + 1
      }
      this.localRanges.push(newRange)
    },

    removeRange (index) {
      this.localRanges.splice(index, 1)
    },
    
    getScopeValidator (field, index) {
      return (value) => {
        if (!value && field === 'max') {
          return true
        }
        
        const currentRange = this.localRanges[index]
        const nextRange = this.localRanges[index + 1]
        const prevRange = this.localRanges[index - 1]

        if (field === 'min') {
          if (prevRange && value <= prevRange.max) {
            return this.$t('minShouldGreaterThanPrevMax')
          }
          if (currentRange.max && value > currentRange.max) {
            return this.$t('minShouldLessThanMax')
          }
        }

        if (field === 'max') {
          if (value < currentRange.min) {
            return this.$t('maxShouldGreaterThanMin')
          }
          if (nextRange && value >= nextRange.min) {
            return this.$t('maxShouldLessThanNextMin')
          }
        }

        return true
      }
    },

    validate () {
      if (this.readonly) {
        return true
      }
      const sortedRanges = [...this.localRanges].sort((a, b) => a.min - b.min)
      for (let i = 0; i < sortedRanges.length - 1; i++) {
        const currentRange = sortedRanges[i]
        const nextRange = sortedRanges[i + 1]
        
        if (currentRange.max && currentRange.max >= nextRange.min) {
          throw new Error(this.$t('quantityRangesOverlap'))
        }
      }

      return true
    }
  }
}
</script>

<style scoped>
.shipping-range {
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 8px;
}
.shipping-range:last-child {
  border-bottom: none;
}

.divider-height {
  max-height: 28px;  
  margin-top: 22px;
}
</style>



// WEBPACK FOOTER //
// src/components/ShippingRangeEditor.vue